// Click-and-drag crop modal for newly-uploaded aerial / Google Earth images.
// Shows the chosen image inside a modal, lets the operator drag a rectangle to
// define the crop region, fine-tune via 8 handles or drag-to-move, lock an
// aspect ratio if needed, and zoom the source for large screenshots. The
// rectangle is then re-exported as a JPEG Blob via natural-pixel canvas
// extraction. Skipping the crop uploads the raw image untouched.
//
// Shortcuts:
//   Esc   → cancel
//   Enter → apply crop (or skip if no rectangle drawn)
//
// All measurements are stored as percentages of the displayed image area so
// the rectangle survives zoom / pan / window resize without recomputation.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Scissors, X, Check, ZoomIn, ZoomOut, Maximize2, RotateCcw } from 'lucide-react';

const ASPECT_RATIOS = [
  { key: 'free', label: 'Free', ratio: null },
  { key: '1:1',  label: '1 : 1',  ratio: 1 },
  { key: '4:3',  label: '4 : 3',  ratio: 4 / 3 },
  { key: '3:2',  label: '3 : 2',  ratio: 3 / 2 },
  { key: '16:9', label: '16 : 9', ratio: 16 / 9 },
];

const HANDLES = [
  { key: 'nw', cursor: 'nwse-resize', x: 0,   y: 0,   ox: -4, oy: -4 },
  { key: 'n',  cursor: 'ns-resize',   x: 0.5, y: 0,   ox:  0, oy: -4 },
  { key: 'ne', cursor: 'nesw-resize', x: 1,   y: 0,   ox:  4, oy: -4 },
  { key: 'e',  cursor: 'ew-resize',   x: 1,   y: 0.5, ox:  4, oy:  0 },
  { key: 'se', cursor: 'nwse-resize', x: 1,   y: 1,   ox:  4, oy:  4 },
  { key: 's',  cursor: 'ns-resize',   x: 0.5, y: 1,   ox:  0, oy:  4 },
  { key: 'sw', cursor: 'nesw-resize', x: 0,   y: 1,   ox: -4, oy:  4 },
  { key: 'w',  cursor: 'ew-resize',   x: 0,   y: 0.5, ox: -4, oy:  0 },
];

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export default function AerialCropModal({ file, onConfirm, onCancel }) {
  const [imgUrl, setImgUrl] = useState(null);
  const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
  const [box, setBox] = useState(null);                  // % coords {x,y,w,h}
  const [aspectKey, setAspectKey] = useState('free');
  const [zoom, setZoom] = useState(1);                   // 1×–4×
  // gesture: { kind:'draw'|'move'|'resize', handle?, sx, sy, startBox }
  const [gesture, setGesture] = useState(null);
  const stageRef = useRef(null);
  const imgRef = useRef(null);

  // Source object URL
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Keyboard shortcuts (Esc cancel · Enter apply)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter')  { e.preventDefault(); applyCropRef.current?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const activeRatio = ASPECT_RATIOS.find(a => a.key === aspectKey)?.ratio || null;

  // Convert client coords → % of stage (accounting for current scroll/zoom).
  const pctFromClient = useCallback((clientX, clientY) => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return { x: 0, y: 0 };
    const r = img.getBoundingClientRect();
    return {
      x: clamp(((clientX - r.left) / r.width) * 100, 0, 100),
      y: clamp(((clientY - r.top)  / r.height) * 100, 0, 100),
    };
  }, []);

  // Apply aspect ratio to a draft box, anchored at (ax,ay).
  // ratio is width/height in pixel space; we convert via image natural dims.
  const enforceAspect = useCallback((nx, ny, nw, nh, ratio, ax, ay) => {
    if (!ratio || !imgDims.w || !imgDims.h) return { x: nx, y: ny, w: nw, h: nh };
    // Convert ratio (pixel-space) → stage-percent ratio (stage is the image
    // itself, so percentage width and height correspond directly to imgDims).
    // The "true" ratio in percent units is (ratio * imgDims.h / imgDims.w).
    const pctRatio = (ratio * imgDims.h) / imgDims.w;
    // Match the longer dimension and shrink the other.
    let w = nw;
    let h = nh;
    if (w / h > pctRatio) {
      w = h * pctRatio;
    } else {
      h = w / pctRatio;
    }
    // Anchor: ax,ay is the corner that should stay put (0=left/top, 1=right/bottom).
    const x = ax === 1 ? nx + nw - w : nx;
    const y = ay === 1 ? ny + nh - h : ny;
    return { x, y, w, h };
  }, [imgDims]);

  // Start drawing a fresh rectangle when clicking on the image background.
  const onStageDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.dataset.role === 'handle' || e.target.dataset.role === 'crop-rect') return;
    e.preventDefault();
    const p = pctFromClient(e.clientX, e.clientY);
    setGesture({ kind: 'draw', sx: p.x, sy: p.y });
    setBox({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const onRectDown = (e) => {
    if (e.button !== 0 || !box) return;
    e.stopPropagation();
    e.preventDefault();
    const p = pctFromClient(e.clientX, e.clientY);
    setGesture({ kind: 'move', sx: p.x, sy: p.y, startBox: { ...box } });
  };

  const onHandleDown = (handle) => (e) => {
    if (e.button !== 0 || !box) return;
    e.stopPropagation();
    e.preventDefault();
    const p = pctFromClient(e.clientX, e.clientY);
    setGesture({ kind: 'resize', handle, sx: p.x, sy: p.y, startBox: { ...box } });
  };

  // Global pointer-move / pointer-up for any in-flight gesture
  useEffect(() => {
    if (!gesture) return;
    const onMove = (e) => {
      const p = pctFromClient(e.clientX, e.clientY);
      if (gesture.kind === 'draw') {
        const nx = Math.min(gesture.sx, p.x);
        const ny = Math.min(gesture.sy, p.y);
        const nw = Math.abs(p.x - gesture.sx);
        const nh = Math.abs(p.y - gesture.sy);
        if (activeRatio) {
          // Anchor at the original click corner
          const ax = p.x >= gesture.sx ? 0 : 1;
          const ay = p.y >= gesture.sy ? 0 : 1;
          setBox(enforceAspect(nx, ny, nw, nh, activeRatio, ax, ay));
        } else {
          setBox({ x: nx, y: ny, w: nw, h: nh });
        }
      } else if (gesture.kind === 'move') {
        const dx = p.x - gesture.sx;
        const dy = p.y - gesture.sy;
        const s = gesture.startBox;
        const x = clamp(s.x + dx, 0, 100 - s.w);
        const y = clamp(s.y + dy, 0, 100 - s.h);
        setBox({ x, y, w: s.w, h: s.h });
      } else if (gesture.kind === 'resize') {
        const s = gesture.startBox;
        const right = s.x + s.w;
        const bottom = s.y + s.h;
        let nx = s.x, ny = s.y, nw = s.w, nh = s.h;
        const h = gesture.handle;
        if (h.includes('w')) { nx = clamp(p.x, 0, right - 1); nw = right - nx; }
        if (h.includes('e')) { nw = clamp(p.x, s.x + 1, 100) - s.x; }
        if (h.includes('n')) { ny = clamp(p.y, 0, bottom - 1); nh = bottom - ny; }
        if (h.includes('s')) { nh = clamp(p.y, s.y + 1, 100) - s.y; }
        if (activeRatio && (h.length === 2)) {
          // Corner resize → keep opposite corner fixed
          const ax = h.includes('w') ? 1 : 0;
          const ay = h.includes('n') ? 1 : 0;
          ({ x: nx, y: ny, w: nw, h: nh } = enforceAspect(nx, ny, nw, nh, activeRatio, ax, ay));
        }
        setBox({ x: nx, y: ny, w: nw, h: nh });
      }
    };
    const onUp = () => setGesture(null);
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
  }, [gesture, pctFromClient, activeRatio, enforceAspect]);

  // Crop / skip
  const applyCrop = useCallback(async () => {
    if (!box || box.w < 1 || box.h < 1) {
      onConfirm(file); return;
    }
    const px = Math.round((box.x / 100) * imgDims.w);
    const py = Math.round((box.y / 100) * imgDims.h);
    const pw = Math.round((box.w / 100) * imgDims.w);
    const ph = Math.round((box.h / 100) * imgDims.h);
    if (pw <= 0 || ph <= 0) { onConfirm(file); return; }
    const canvas = document.createElement('canvas');
    canvas.width = pw;
    canvas.height = ph;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgRef.current, px, py, pw, ph, 0, 0, pw, ph);
    canvas.toBlob((blob) => {
      if (!blob) return onConfirm(file);
      const croppedFile = new File(
        [blob],
        (file.name || 'aerial').replace(/\.\w+$/, '') + '_cropped.jpg',
        { type: 'image/jpeg' },
      );
      onConfirm(croppedFile);
    }, 'image/jpeg', 0.9);
  }, [box, file, imgDims, onConfirm]);

  // Allow Enter shortcut to grab the latest applyCrop
  const applyCropRef = useRef(applyCrop);
  useEffect(() => { applyCropRef.current = applyCrop; }, [applyCrop]);

  // Aspect-ratio change snaps current box to the new ratio (if any)
  useEffect(() => {
    if (!box || !activeRatio || !imgDims.w) return;
    setBox(b => {
      if (!b) return b;
      const adjusted = enforceAspect(b.x, b.y, b.w, b.h, activeRatio, 0, 0);
      // Don't loop the effect — only update when meaningfully different
      if (Math.abs(adjusted.w - b.w) < 0.01 && Math.abs(adjusted.h - b.h) < 0.01) return b;
      return adjusted;
    });
    // intentionally not depending on `box` to avoid feedback loop
  }, [aspectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!file) return null;

  const stagePixel = box && imgDims.w ? {
    pw: Math.round((box.w / 100) * imgDims.w),
    ph: Math.round((box.h / 100) * imgDims.h),
  } : null;

  return (
    <div
      data-testid="aerial-crop-modal"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
    >
      <div className="bg-white rounded-md shadow-xl max-w-6xl w-full overflow-hidden flex flex-col" style={{ maxHeight: '95vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Scissors size={14} className="text-[#D35400]" /> Crop the aerial
          </h3>
          <button
            data-testid="aerial-crop-close"
            onClick={onCancel}
            className="p-1 hover:bg-slate-100 rounded"
            title="Close (Esc)"
          ><X size={14} /></button>
        </div>

        {/* Toolbar — aspect ratio + zoom */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50">
          <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Aspect</span>
          <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Aspect ratio">
            {ASPECT_RATIOS.map(a => (
              <button
                key={a.key}
                data-testid={`aerial-crop-aspect-${a.key}`}
                onClick={() => setAspectKey(a.key)}
                className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                  aspectKey === a.key
                    ? 'bg-[#D35400] text-white border-[#D35400]'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >{a.label}</button>
            ))}
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Zoom</span>
          <button
            data-testid="aerial-crop-zoom-out"
            onClick={() => setZoom(z => Math.max(1, +(z - 0.25).toFixed(2)))}
            disabled={zoom <= 1}
            className="p-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-white"
            title="Zoom out"
          ><ZoomOut size={12} /></button>
          <span className="text-[11px] font-mono w-10 text-center text-slate-600">{zoom.toFixed(2)}×</span>
          <button
            data-testid="aerial-crop-zoom-in"
            onClick={() => setZoom(z => Math.min(4, +(z + 0.25).toFixed(2)))}
            disabled={zoom >= 4}
            className="p-1 rounded border border-slate-200 disabled:opacity-40 hover:bg-white"
            title="Zoom in"
          ><ZoomIn size={12} /></button>
          <button
            data-testid="aerial-crop-zoom-fit"
            onClick={() => setZoom(1)}
            className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-white"
            title="Fit to window (reset zoom)"
          ><Maximize2 size={10} /> Fit</button>
          <div className="ml-auto text-[11px] text-slate-500 hidden md:block">
            <strong>Click-drag</strong> to draw · drag handles to resize · <kbd className="px-1 py-0.5 border rounded bg-white text-[10px]">Esc</kbd> cancel · <kbd className="px-1 py-0.5 border rounded bg-white text-[10px]">Enter</kbd> apply
          </div>
        </div>

        {/* Stage */}
        <div className="relative flex-1 overflow-auto bg-slate-900 select-none" style={{ minHeight: '55vh' }}>
          <div
            ref={stageRef}
            data-testid="aerial-crop-stage"
            onPointerDown={onStageDown}
            className="relative cursor-crosshair"
            style={{
              width: `${zoom * 100}%`,
              minWidth: '100%',
              minHeight: '55vh',
            }}
          >
            {imgUrl && (
              <img
                ref={imgRef}
                src={imgUrl}
                alt="To crop"
                draggable={false}
                onLoad={(e) => setImgDims({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                className="block w-full h-auto pointer-events-none"
                style={{ userSelect: 'none' }}
              />
            )}

            {box && box.w > 0 && box.h > 0 && (
              <>
                {/* Four-rect mask outside the crop region */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute bg-black/55" style={{ left: 0, top: 0, right: 0, height: `${box.y}%` }} />
                  <div className="absolute bg-black/55" style={{ left: 0, top: `${box.y + box.h}%`, right: 0, bottom: 0 }} />
                  <div className="absolute bg-black/55" style={{ left: 0, top: `${box.y}%`, width: `${box.x}%`, height: `${box.h}%` }} />
                  <div className="absolute bg-black/55" style={{ left: `${box.x + box.w}%`, top: `${box.y}%`, right: 0, height: `${box.h}%` }} />
                </div>

                {/* Crop rectangle (draggable from inside) */}
                <div
                  data-role="crop-rect"
                  data-testid="aerial-crop-rect"
                  onPointerDown={onRectDown}
                  className="absolute ring-2 ring-[#D35400]"
                  style={{
                    left: `${box.x}%`,
                    top: `${box.y}%`,
                    width: `${box.w}%`,
                    height: `${box.h}%`,
                    cursor: gesture?.kind === 'move' ? 'grabbing' : 'grab',
                  }}
                >
                  {/* Size badge */}
                  {stagePixel && (
                    <span
                      data-testid="aerial-crop-dims"
                      className="absolute -top-5 left-0 text-[10px] bg-[#D35400] text-white px-1.5 py-0.5 rounded font-mono whitespace-nowrap"
                    >
                      {stagePixel.pw} × {stagePixel.ph} px
                    </span>
                  )}
                  {/* Rule-of-thirds guides */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 bottom-0 left-1/3 w-px bg-white/30" />
                    <div className="absolute top-0 bottom-0 left-2/3 w-px bg-white/30" />
                    <div className="absolute left-0 right-0 top-1/3 h-px bg-white/30" />
                    <div className="absolute left-0 right-0 top-2/3 h-px bg-white/30" />
                  </div>
                  {/* 8 resize handles */}
                  {HANDLES.map(h => (
                    <span
                      key={h.key}
                      data-role="handle"
                      data-testid={`aerial-crop-handle-${h.key}`}
                      onPointerDown={onHandleDown(h.key)}
                      className="absolute w-2 h-2 bg-white border border-[#D35400] rounded-sm"
                      style={{
                        left: `calc(${h.x * 100}% + ${h.ox}px)`,
                        top:  `calc(${h.y * 100}% + ${h.oy}px)`,
                        transform: 'translate(-50%, -50%)',
                        cursor: h.cursor,
                      }}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-2 px-4 py-2 border-t border-slate-200 bg-slate-50">
          <button
            data-testid="aerial-crop-clear"
            onClick={() => setBox(null)}
            disabled={!box}
            className="flex items-center gap-1 text-xs px-3 py-1.5 border border-slate-200 rounded text-slate-600 disabled:opacity-40 hover:bg-white"
          ><RotateCcw size={12} /> Clear rectangle</button>
          {box && stagePixel && (
            <span className="text-[11px] text-slate-500 hidden md:inline-flex items-center gap-1">
              Output: <strong className="font-mono">{stagePixel.pw} × {stagePixel.ph} px</strong>
              {activeRatio && <span className="text-[10px] text-slate-400">· {ASPECT_RATIOS.find(a => a.key === aspectKey).label}</span>}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="aerial-crop-skip"
              onClick={() => onConfirm(file)}
              className="text-xs px-3 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-white"
            >Skip &amp; upload as-is</button>
            <button
              data-testid="aerial-crop-apply"
              onClick={applyCrop}
              disabled={!box || box.w < 1 || box.h < 1}
              className="flex items-center gap-1 text-xs px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded font-semibold disabled:opacity-40"
            >
              <Check size={12} /> {box && box.w >= 1 ? 'Crop & upload' : 'Draw rectangle first'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
